import { useEffect, useMemo, useState } from 'react';

import useOffers from 'api/useOffers';
import { Offer } from 'common/contracts';
import { OfferType } from 'constants/enums';
import { SettingsTabProps } from 'views/RollingOffers/types';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';
import SegmentInput from 'components/SegmentInput/SegmentInput';
import { Separator } from 'components/ui/separator';

const SettingsTab: React.FC<SettingsTabProps> = ({
  formikProps,
  rollingOfferId,
  edit,
  chosenSegment,
  setChosenSegment,
  offerDesignItems,
  data,
  dup
}) => {
  const { handleChange, handleBlur, touched, errors, setFieldValue, values } =
    formikProps;

  const [isNameExists, setIsNameExists] = useState<boolean>(false);

  const { getOffers } = useOffers({
    offerId: rollingOfferId,
    offerType: OfferType.ROLLING_OFFER
  });

  const offerDesignOptions = useMemo(
    () =>
      offerDesignItems?.map((item) => ({
        value: item.value,
        label: item.content
      })) || [],
    [offerDesignItems]
  );

  const selectedOfferDesign = useMemo(() => {
    const selected = offerDesignItems?.find(
      (item) => item.value === values.offerUiId
    );
    return selected
      ? { value: selected.value, label: selected.content }
      : undefined;
  }, [offerDesignItems, values.offerUiId]);

  const isNameError = (touched.name && errors.name) || isNameExists;
  const nameErrorMessage =
    (touched.name ? errors.name?.toString() : '') ||
    (isNameExists ? 'Name already exists' : '');

  const isExternalIdError =
    Boolean(errors.publisherOfferId) && (touched.publisherOfferId || dup);
  const externalIdErrorMessage =
    touched.publisherOfferId || dup ? errors.publisherOfferId?.toString() : '';

  const offerDesignErrorMessage =
    touched.offerUiId && errors.offerUiId ? errors.offerUiId?.toString() : '';

  useEffect(() => {
    const offerExists = getOffers.data?.offers.some(
      (offer: Offer) => offer.name === values.name
    );
    setIsNameExists(offerExists && !edit);
  }, [values.name, edit, getOffers.data]);

  useEffect(() => {
    if (data?.segments) {
      setChosenSegment(data.segments);
    }
  }, [data?.segments, setChosenSegment]);

  return (
    <div className="flex flex-col">
      <RichText
        title="General"
        description="Complete the required fields for your offer."
        className="mb-5"
      />
      <div className="flex flex-col gap-9">
        <div className="grid grid-cols-2 gap-5">
          <Input
            id="rolling-offer-name"
            label="Name"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            placeholder="Name"
            state={isNameError ? 'error' : 'default'}
            description={nameErrorMessage}
            dataTestId="rollingOfferNameInput"
          />
          <Input
            id="rolling-offer-external-id"
            label="External ID"
            name="publisherOfferId"
            value={values.publisherOfferId}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            placeholder="External ID"
            state={isExternalIdError ? 'error' : 'default'}
            description={externalIdErrorMessage}
            dataTestId="rollingOfferIdInput"
          />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <InputSelectDropdown
            label="Offer Design"
            isRequired
            placeholder="Select"
            listDataTestId="dashRollingOfferDesignList"
            buttonDataTestId="rollingOfferDesignBox"
            options={offerDesignOptions}
            defaultSelected={selectedOfferDesign}
            onOptionChange={(value) => setFieldValue('offerUiId', value)}
            description={offerDesignErrorMessage}
          />
          <Input
            id="rolling-offer-priority"
            label="Priority"
            name="priority"
            value={values.priority}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            type="number"
            placeholder="1"
            min={1}
            max={10}
            tooltipIcon="fa-circle-info"
            infoTooltipContent="Priority decides which rolling offer show, based on their importance. The lower the number, the higher the priority."
          />
        </div>
      </div>
      <Separator className="my-5 bg-base-border" />
      <div data-testid="rollingOfferSegmentsList">
        <SegmentInput
          value={chosenSegment}
          onChange={(segments) => {
            setChosenSegment(segments);
            setFieldValue('segments', segments);
          }}
          richTextDescription="Create or add players segments to your offer."
          dataTestId="rollingOfferSegmentsInput"
        />
      </div>
      <Separator className="my-5 bg-base-border" />
    </div>
  );
};

export default SettingsTab;
